import _ from "lodash";
/**
 * Name: EmojiPicker
 * Type: UI Component
 * Function: A reusable UI component to show emoji picker.
 * It uses emoji-mart (https://github.com/missive/emoji-mart)
 * and react-tiny-popover (https://github.com/alexkatz/react-tiny-popover) as dependencies.
 * Takes a child (react component or HTMLElement) that react-tiny-popover uses as target.
 * Emoji Picker Popup will be attached around this passed child.
 * Output: Renders an emoji picker as a popup.
 */

import React, { useState, useEffect } from "react";
import classes from "./EmojiPicker.scss";
import PropTypes from "prop-types";
import { Popover } from "@toddle-edu/react-tiny-popover";

const EmojiPicker = ({
  onEmojiSelect,
  isOpen,
  pickerProps,
  popoverProps,
  children,
  onClickOutside,
  ...props
}) => {
  const [LazyEmojiPicker, setLazyEmojiPicker] = useState();

  // list of emoji unified codes that needs to be excluded from the picker
  const excludedEmojiIDs = [
    "1F595", // "Reversed Hand with Middle Finger Extended"
  ];

  useEffect(() => {
    /*
      - This function is custom implementation of React lazy.
      - Picker component from emoji-mart will be loaded only when this component renders.
      - After Picker component is loaded, it is set as state of this component (LazyEmojiPicker).
    */
    const loadEmojiPicker = async () => {
      try {
        await import(
          /* webpackChunkName: "emojipicker" */ "emoji-mart/css/emoji-mart.css"
        );
        await import(/* webpackChunkName: "emojipicker" */ "emoji-mart").then(
          module => {
            setLazyEmojiPicker(() => module.Picker);
          }
        );
      } catch (e) {
        window.location.reload();
      }
    };
    loadEmojiPicker();
  }, []);

  /**
    Please NOTE: Do not remove the below check. It is required for the following reason:
    To prevent flickering during the rendering of the emoji picker,
    The popover should be rendered only after the LazyEmojiPicker is loaded.

    Issue if not done this way: Initially, when the LazyEmojiPicker component is undefined,
    the popover has nothing to render, so it assumes that nothing is touching the boundary.
    As a result, the initial rendered position may appear acceptable.
    However, once the actual emoji picker is rendered after LazyEmojiPicker is loaded,
    its width and height can cause it to extend beyond the boundary.
    This leads to frequent repositioning, causing a flickering effect.
   */
  if (!LazyEmojiPicker) return null;

  return (
    // ASK: this
    <Popover
      containerClassName={classes.popover}
      transitionDuration={0.1}
      {...popoverProps}
      isOpen={isOpen}
      content={
        LazyEmojiPicker && (
          <div className={classes.pickerContainer}>
            <LazyEmojiPicker
              {...pickerProps}
              emojisToShowFilter={emoji =>
                !_.includes(excludedEmojiIDs, emoji?.unified)
              }
              onSelect={onEmojiSelect}
            />
          </div>
        )
      }
      onClickOutside={onClickOutside}
    >
      {children}
    </Popover>
  );
};

EmojiPicker.propTypes = {
  /**
   * Function that will be invoked when user selects an emoji from the picker
   * and will contain emoji information (Object) as an argument.
   */
  onEmojiSelect: PropTypes.func,
  /**
   * Boolean to show emoji picker
   */
  isOpen: PropTypes.bool,
  /**
   * Props that will be passed down to the emoji picker.
   * Check valid props here -> (https://github.com/missive/emoji-mart#picker)
   */
  pickerProps: PropTypes.object,
  /**
   * Props that will be passed down to the popover.
   * Check valid props here -> (https://github.com/alexkatz/react-tiny-popover#api)
   */
  popoverProps: PropTypes.object,
  /**
   * React component or HTMLElement, popover will use to track
   * Check children prop here -> (https://github.com/alexkatz/react-tiny-popover#api)
   */
  children: PropTypes.node,
  /**
   * Will be fired when clicked outside the emoji picker or target element
   */
  onClickOutside: PropTypes.func,
};

EmojiPicker.defaultProps = {
  isOpen: false,
};

export default EmojiPicker;
